import { Behavior } from '../entities/Behavior';

const FULL_CIRCLE = Math.PI * 2;
const DEFAULT_MINIMUM = 0.9;
const DEFAULT_MAXIMUM = 1.1;
const DEFAULT_CYCLES_PER_SECOND = 1;

/** How a {@link PulseScale} breathes. */
export interface PulseScaleOptions {
  /** Scale at the bottom of the cycle. */
  readonly minimum?: number;
  /** Scale at the top of the cycle. */
  readonly maximum?: number;
  /** Full cycles per second. */
  readonly cyclesPerSecond?: number;
  /** Seconds to offset the wave by, so several entities do not pulse in lockstep. */
  readonly phaseSeconds?: number;
}

/**
 * Breathes its entity between two scales on a sine wave.
 *
 * The engine's answer to "make it look tappable". A gently pulsing button reads as interactive to a
 * child who cannot read the label on it, and doing it here means no game reimplements the same wave.
 *
 * It owns the elapsed time rather than reading the clock, so a paused or newly attached entity starts
 * its cycle where it left off rather than wherever the world clock happens to be.
 */
export class PulseScale extends Behavior {
  readonly #minimum: number;
  readonly #maximum: number;
  readonly #cyclesPerSecond: number;

  #elapsedSeconds: number;

  constructor(options: PulseScaleOptions = {}) {
    super();
    this.#minimum = options.minimum ?? DEFAULT_MINIMUM;
    this.#maximum = options.maximum ?? DEFAULT_MAXIMUM;
    this.#cyclesPerSecond = options.cyclesPerSecond ?? DEFAULT_CYCLES_PER_SECOND;
    this.#elapsedSeconds = options.phaseSeconds ?? 0;
  }

  /** Advances the wave and writes the resulting uniform scale. */
  override onUpdate(deltaSeconds: number): void {
    this.#elapsedSeconds += deltaSeconds;

    const wave = (Math.sin(this.#elapsedSeconds * this.#cyclesPerSecond * FULL_CIRCLE) + 1) / 2;
    const scale = this.#minimum + (this.#maximum - this.#minimum) * wave;
    this.entity.transform.scale.set(scale, scale);
  }
}
